import { Building2, Users, FlaskConical, Sigma, Target, BrainCircuit, CheckCircle2, Loader2, CircleDashed, type LucideIcon } from "lucide-react";
import { SectionLabel, H2, Body } from "../shared/Typography";

const VIOLET = "text-[#5B21B6] dark:text-[#B9A5FF]";
const CARD = "rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10]";

// Illustrative activity log: the three states the product tracks (submitted, in progress, finished).
const ACTIVITY: { kind: string; item: string; status: "Finished" | "In progress" | "Submitted" }[] = [
  { kind: "Cohort", item: "Digital twins · criteria v3", status: "Finished" },
  { kind: "Simulation", item: "3+3 dose escalation", status: "In progress" },
  { kind: "Model", item: "SBML conversion", status: "Submitted" },
  { kind: "Workbench", item: "Results review in iViz", status: "Finished" },
];

const STATUS_STYLE = {
  Finished: { icon: CheckCircle2, cls: "text-[#2D8A5E] dark:text-[#3FA37A] bg-[#2D8A5E]/10 dark:bg-[#3FA37A]/15" },
  "In progress": { icon: Loader2, cls: "text-[#5B21B6] dark:text-[#B9A5FF] bg-[#5B21B6]/10 dark:bg-[#7C4DFF]/15" },
  Submitted: { icon: CircleDashed, cls: "text-[#5C5C66] dark:text-[#9A9A9A] bg-[#0A0A0F]/5 dark:bg-white/10" },
} as const;

function ActivityLog() {
  return (
    <div className="rounded-2xl border border-[#0A0A0F]/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-black/40 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#0A0A0F]/10 dark:border-white/10">
        <span className="text-xs font-medium text-[#0A0A0F] dark:text-white">Activity · Oncology workspace</span>
        <span className="text-[10px] uppercase tracking-widest text-[#5C5C66] dark:text-[#9A9A9A]">Live</span>
      </div>
      <ul className="divide-y divide-[#0A0A0F]/10 dark:divide-white/10">
        {ACTIVITY.map(({ kind, item, status }) => {
          const { icon: Icon, cls } = STATUS_STYLE[status];
          return (
            <li key={item} className="flex items-center justify-between gap-3 px-5 py-3.5">
              <span className="min-w-0">
                <span className={`block text-[10px] font-bold uppercase tracking-widest ${VIOLET}`}>{kind}</span>
                <span className="block text-sm text-[#0A0A0F] dark:text-white truncate">{item}</span>
              </span>
              <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${cls}`}>
                <Icon size={12} className={status === "In progress" ? "animate-spin motion-reduce:animate-none" : ""} />{status}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const AUDIENCE: { title: string; body: string; icon: LucideIcon }[] = [
  { title: "Clinical pharmacologists and modelers", body: "Bring the models you already have and see how they behave in a trial.", icon: FlaskConical },
  { title: "Biostatisticians and trial designers", body: "Test escalation rules and decision criteria across many repetitions.", icon: Sigma },
  { title: "Clinical development teams", body: "Stress-test a design before committing to it.", icon: Target },
];

export function TeamsAndAudience() {
  return (
    <div className="space-y-6">
      <div className={`${CARD} p-8 md:p-12 grid lg:grid-cols-2 gap-10 items-center`}>
        <div>
          <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest ${VIOLET}`}><Building2 size={16} />Built for teams</p>
          <H2 className="mt-3 mb-5">One place for every cohort, model and run.</H2>
          <Body>
            Work is organized by organization and therapeutic area, and workspaces are flexible. Digital
            twin cohorts, simulations and workbenches are tracked in one place, with an activity log showing
            what was submitted, what finished and what is in progress.
          </Body>
        </div>
        <ActivityLog />
      </div>
      <div>
        <p className={`flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-4 ${VIOLET}`}><Users size={16} />Who it&apos;s for</p>
        <div className="grid md:grid-cols-3 gap-6">
          {AUDIENCE.map(({ title, body, icon: Icon }) => (
            <div key={title} className={`${CARD} p-7`}>
              <span className="inline-flex w-11 h-11 items-center justify-center rounded-xl bg-[#5B21B6]/10 dark:bg-[#7C4DFF]/15"><Icon size={20} className={VIOLET} /></span>
              <p className="mt-5 text-lg font-normal tracking-[-0.01em] text-[#0A0A0F] dark:text-white">{title}</p>
              <p className="mt-2 text-sm leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Restored from the previous Foresight page, in a card layout.
const USE_CASES: { icon: LucideIcon; title: string; body: string }[] = [
  { icon: Target, title: "Dose Selection & Escalation", body: "Run thousands of virtual dose escalation scenarios to identify the safest, most efficacious dose before Phase I begins, eliminating costly trial-and-error in humans." },
  { icon: Users, title: "Protocol & Population Optimisation", body: "Identify the subpopulation most likely to respond and model protocol amendments before locking the SAP and committing to a design." },
  { icon: BrainCircuit, title: "CRO & Sponsor Partnerships", body: "Integrate Foresight directly into your CRO workflow. Deliver simulation-backed trial designs to sponsors as a measurable competitive differentiator." },
];

export function UseCases() {
  return (
    <div>
      <div className="text-center max-w-3xl mx-auto mb-12">
        <div className="flex justify-center"><SectionLabel tone="foresight">Use Cases</SectionLabel></div>
        <H2>Supporting sponsors from IND to approval</H2>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {USE_CASES.map(({ icon: Icon, title, body }, i) => (
          <div
            key={title}
            className="group relative overflow-hidden rounded-3xl border border-[#0A0A0F]/10 dark:border-white/10 bg-white dark:bg-[#0D0D10] p-8 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#5B21B6]/40 dark:hover:border-[#7C4DFF]/40 hover:shadow-[0_24px_60px_-24px_rgba(124,77,255,0.45)]"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#5B21B6] via-[#7C4DFF] to-[#B9A5FF] opacity-60 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-start justify-between">
              <span className="inline-flex w-12 h-12 items-center justify-center rounded-2xl bg-[#5B21B6]/10 dark:bg-[#7C4DFF]/15"><Icon size={22} className={VIOLET} /></span>
              <span className="font-display text-5xl font-medium text-[#0A0A0F]/[0.07] dark:text-white/[0.08]">0{i + 1}</span>
            </div>
            <p className="mt-8 text-xl font-normal tracking-[-0.01em] text-[#0A0A0F] dark:text-white">{title}</p>
            <p className="mt-3 text-sm leading-relaxed text-[#5C5C66] dark:text-[#9A9A9A]">{body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
